import { useEffect, useState } from 'react';

import { DeleteButton } from '@/components-ant/button';
import { Form } from '@/components-ant/form';
import { FormModal } from '@/components-ant/form-modal';
import { Input } from '@/components-ant/input';
import { Result } from '@/shared/helpers/Result';

import { BaseDataTable } from '../BaseDataTable';
import { UpdateViewInput, ViewTab } from './types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  savedViews: ViewTab[];
  onUpdateView: (input: Omit<UpdateViewInput, 'filters'>) => Promise<Result>;
  onDeleteView: (viewId: string) => Promise<Result>;
};

export function ViewConfigurationModal({
  isOpen,
  onClose,
  savedViews,
  onUpdateView,
  onDeleteView,
}: Props) {
  const [deletingIds, setDeletingIds] = useState(new Set<string>());
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    setDeletingIds(new Set());
    form.resetFields();
  }, [isOpen, form]);

  const submit = async (titlesById: Record<string, string>) => {
    setIsSaving(true);
    const deleteResults = await Promise.all(Array.from(deletingIds).map((id) => onDeleteView(id)));

    const updateResults = await Promise.all(
      savedViews
        .filter((view) => titlesById[view.id] && view.title !== titlesById[view.id].trim())
        .map((view) => onUpdateView({ id: view.id, name: titlesById[view.id].trim() })),
    );

    if (
      deleteResults.every((result) => result.isSuccess) &&
      updateResults.every((result) => result.isSuccess)
    ) {
      onClose();
    }
    setIsSaving(false);
  };

  return (
    <FormModal
      title="Manage saved views"
      open={isOpen}
      onCancel={onClose}
      isSaving={isSaving}
      onSave={() => form.submit()}
    >
      <Form
        name="viewConfiguration"
        form={form}
        initialValues={Object.fromEntries(savedViews.map((view) => [view.id, view.title]))}
        onFinish={submit}
      >
        <BaseDataTable
          rows={savedViews.filter((view) => !deletingIds.has(view.id))}
          simple
          columns={[
            {
              key: 'title',
              title: 'Name',
              render: (title, { id }) => (
                <Form.Item name={id}>
                  <Input aria-label={`Name for view "${title}"`} />
                </Form.Item>
              ),
            },
            {
              key: 'id',
              title: '',
              align: 'right',
              render: (viewId: string, { title }) => (
                <DeleteButton
                  aria-label={`Delete view "${title}"`}
                  onClick={() => setDeletingIds((prev) => new Set(prev).add(viewId))}
                />
              ),
            },
          ]}
        />
      </Form>
    </FormModal>
  );
}
